<template>
    <div class="template-category-widgets">
        <div
            v-if="!!templateHelpData"
            class="w-full h-full"
        >
            <WidgetTemplateHelpInfo
                :template-help="templateHelpData"
                @close="onCloseHelpInfo"
            />
        </div>
        <div
            v-else
            class="w-full h-ful"
        >
            <WidgetTemplateCategoryWidgetList
                :category="category"
                :total-widgets="totalWidgets"
                :selected-templates="selectedTemplatesModel"
                @template:select="onTemplateSelect"
                @template:count="onChangeTemplateCounter"
                @step:next="onNextStep"
                @step:prev="onPrevStep"
                @template:info="openTemplateHelpInfo"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useVModel } from '@vueuse/core'
import { IWidgetTemplateCategory, IWidgetTemplateListItem } from '@/types/templates'
import {
    MAX_WIDGETS_LIMIT,
    TEMPLATE_CATEGORIES,
    TEMPLATES_SETUP
} from '@/components/RedesignComponents/Widgets/widgets.enum'
import WidgetTemplateCategoryWidgetList
    from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateCategoryWidgetList.vue'
import { TemplateHelp } from '@/types/template-help'
import WidgetTemplateHelpInfo from '@/components/RedesignComponents/Widgets/Common/WidgetTemplateHelpInfo.vue'

const props = defineProps<{
    category?: IWidgetTemplateCategory
    value?: Record<number, { count: number, template: IWidgetTemplateListItem }>
    totalWidgets?: number
}>()
const emit = defineEmits<{
    (e: 'input', pl: Record<number, { count: number, template: IWidgetTemplateListItem }>): void,
    (e: 'update:step', pl: number): void
}>()

/* Data */
const templateHelpData = ref<TemplateHelp | undefined>(undefined)
const selectedTemplatesModel = useVModel(props, 'value', emit, { eventName: 'input' })
/* Computed */
const isTotalError = computed(() => {
    return (props.totalWidgets || 0) >= MAX_WIDGETS_LIMIT
})

/* Methods */
function onTemplateSelect (widgetTemplate: IWidgetTemplateListItem) {
    if (!widgetTemplate.TemplateID) {
        return
    }
    const data = { ...selectedTemplatesModel.value }
    if (data[widgetTemplate.TemplateID]) {
        delete data[widgetTemplate.TemplateID]
    } else if (!isTotalError.value) {
        data[widgetTemplate.TemplateID] = {
            template: { ...widgetTemplate },
            count: 1
        }
    }
    selectedTemplatesModel.value = { ...data }
}

function onChangeTemplateCounter (val: number, templateId: number) {
    if (!selectedTemplatesModel.value?.[templateId]) {
        return
    }
    const data = { ...selectedTemplatesModel.value }
    if (val <= 0) {
        delete data[templateId]
    } else {
        data[templateId] = { ...data[templateId], count: val }
    }
    selectedTemplatesModel.value = data
}

function onPrevStep () {
    emit('update:step', TEMPLATE_CATEGORIES)
    selectedTemplatesModel.value = {}
}

function onNextStep () {
    emit('update:step', TEMPLATES_SETUP)
}

function openTemplateHelpInfo (templateHelp: TemplateHelp) {
    templateHelpData.value = { ...templateHelp }
}

function onCloseHelpInfo () {
    templateHelpData.value = undefined
}

</script>
